import React, { useState } from 'react';
import {
	Box,
	Button,
	Typography,
	useMediaQuery,
	useTheme,
	Link,
	Grid,
	Divider,
	CircularProgress,
	IconButton,
	Stack,
} from '@mui/material';
import { styled } from '@mui/system';
import { Link as RLink } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { RiCheckFill, RiInformationLine } from 'react-icons/ri';
import {
	AddOn,
	AddOnChannel,
	addonRequiresManualSetup,
	AllAddOns,
	getBlockValue,
	getRemainingBlockSlots,
	getTotalBlockSlots,
	getUsedBlockSlots,
	isAddOnInstalled,
	isLitePlan,
	PlanDefinitions,
} from 'common/modules/plans';
import { useTranslation, Trans } from 'services/localization';
import { getAddOnDetails } from 'common/modules/plans/add-ons/details';
import { getAddOnIcon } from 'common/modules/plans/add-ons/getAddOnIcon';
import * as ShopSelectors from 'selectors/ShopSelectors';
import * as NotificationActions from 'actions/NotificationActions';
import * as ShopActions from 'actions/ShopActions';
import * as ViewActions from 'actions/ViewActions';
import { getAddOnSetupPage, getAddOnValueProps } from 'services/addOns';
import { push, replace, Routes } from 'routing';
import { Callable } from 'common/frontend/callable';
import BackdropDialog from 'common/components/BackdropDialog';
import { range } from 'lodash';
import FeatureButton from 'components/features/FeatureButton';
import CustomTooltip from 'components/CustomTooltip';
import ExternalLink from 'components/ExternalLink';
import { newBlockInstalledEvent } from 'common/services/analytics';

interface Props {
	addOn: AddOn;
	addOnChannel: AddOnChannel;
	onClose: () => void;
	nextRoute?: string;
	dividerType?: 'background' | 'divider';
}
const AddOnModalContent = (props: Props) => {
	const { addOn, onClose, addOnChannel } = props;
	const { t } = useTranslation();
	const dispatch = useDispatch();
	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down('md'));

	const [uninstallDialogOpen, setUninstallDialogOpen] = useState<boolean>(false);
	const [loading, setLoading] = useState<boolean>(false);

	const shopFeatures = useSelector(ShopSelectors.shopFeatures);
	const shopPlan = useSelector(ShopSelectors.shopPlan);
	const shopPlanName = shopPlan ? PlanDefinitions[shopPlan].name : '';
	const shopId = useSelector(ShopSelectors.activeShopId)!;

	const allAddOnsAmount = AllAddOns.length;

	const addOnDetails = getAddOnDetails(addOn, t);
	const addOnIcon = getAddOnIcon(addOn);
	const addOnValueProps = getAddOnValueProps(addOn);
	const isIntegration = addonRequiresManualSetup(addOn);
	const setupPage = getAddOnSetupPage(addOn);
	const currentBlockCost = getBlockValue(addOn);
	const isLite = isLitePlan(shopPlan);

	const isInstalled = isAddOnInstalled(addOn, shopFeatures);

	const remainingBlocks = getRemainingBlockSlots(shopFeatures);
	const totalBlocks = getTotalBlockSlots(shopFeatures);
	const usedBlocks = getUsedBlockSlots(shopFeatures);

	const unlimitedBlockSlots = Infinity;

	const handleUninstall = async () => {
		await Callable.plans.addOns.disable({ shopId, addOns: [addOn] });
		onClose();
		dispatch(
			NotificationActions.showNotification({
				message: t('account.addOns.installedAddOns.uninstalled', 'The block was uninstalled'),
				variant: 'info',
			}),
		);
	};

	const handleInstall = async () => {
		setLoading(true);
		try {
			await Callable.plans.addOns.enable({ shopId, addOns: [addOn] });
			dispatch(ShopActions.addInstalledAddOn(addOn));
			newBlockInstalledEvent({ block: addOn, channel: addOnChannel });
			onClose();
			replace(setupPage);
			dispatch(ViewActions.openInstalledAddOnModal(addOn));
		} catch (err) {
			dispatch(NotificationActions.showNotification({ message: err.message, variant: 'error' }));
		}
		setLoading(false);
	};

	const renderHelpArticleLink = () =>
		addOnDetails.supportArticle && (
			<Box mb={1.5}>
				<Typography>
					<ExternalLink href={addOnDetails.supportArticle}>
						<span style={{ fontWeight: 400, textDecoration: 'underline' }}>
							{t('account.addOns.installedAddOns.helpArticle', 'Help article')}
						</span>
					</ExternalLink>
				</Typography>
			</Box>
		);

	const renderBlockSlotSquares = () => {
		if (totalBlocks === unlimitedBlockSlots) return;

		return range(0, totalBlocks).map((_, idx) => {
			const isUsed = idx < usedBlocks;
			const isHighlighted = isInstalled
				? idx < usedBlocks && idx >= usedBlocks - currentBlockCost
				: idx >= usedBlocks && idx < usedBlocks + currentBlockCost;

			return (
				<Box
					key={idx}
					sx={{
						width: '30px',
						height: '30px',
						background: isHighlighted
							? theme.palette.success.lightest
							: isUsed
							? theme.palette.background.secondary
							: theme.palette.background.paper,
						color: theme.palette.text.primary,
						borderRadius: theme.spacing(0.5),
						border: isHighlighted
							? `1px dashed ${theme.palette.success.main}`
							: isUsed
							? 'none'
							: `1px dashed ${theme.palette.border.inputOutline}`,
						display: 'flex',
						flexDirection: 'row',
						justifyContent: 'center',
						alignItems: 'center',
						margin: theme.spacing(0.5),
					}}
				>
					{isUsed && <RiCheckFill size={16} color="inherit" />}
				</Box>
			);
		});
	};

	return (
		<>
			<Grid
				container
				sx={{
					width: '100%',
				}}
			>
				<Grid
					sx={{
						padding: theme.spacing(5.25, 5.25, !isMobile ? 5.25 : 0, 5.25),
						background: !isMobile
							? theme.palette.background.default
							: theme.palette.background.paper,
					}}
					item
					xs={!isMobile ? 7 : 12}
				>
					<Box
						sx={{
							background: theme.palette.background.paper,
							width: 'fit-content',
						}}
						mb={4}
					>
						<StyledImage src={addOnIcon ?? undefined} alt={addOnDetails.name} />
					</Box>
					<Box mb={4}>
						<Typography mb={1} variant="h5">
							{addOnDetails?.name}{' '}
						</Typography>
						<Typography mb={4}>{addOnDetails.description}.</Typography>

						{isIntegration && (
							<Typography>
								{t(
									'account.addOns.modal.bookAMeeting',
									'Book a meeting with our sales representative to go through the details to enable the integration',
								)}
								.
							</Typography>
						)}
						{!isMobile && (
							<ul style={{ padding: theme.spacing(0, 0, 0, 2.5) }}>
								{addOnValueProps.map((vp, idx) => (
									<Box m={1}>
										<li key={idx}>
											<Typography>{vp.description}</Typography>
										</li>
									</Box>
								))}
							</ul>
						)}
					</Box>
				</Grid>

				{isMobile && (
					<Box sx={{ width: '100%' }} mx={4.5}>
						<Divider />
					</Box>
				)}

				<Grid
					sx={{
						padding: theme.spacing(5.25),
						display: 'flex',
						flexDirection: 'column',
						justifyContent: 'flex-start',
					}}
					item
					xs={!isMobile ? 5 : 12}
				>
					<Box mb={2}>
						{remainingBlocks > 0 ? (
							<Box
								ml={-0.5}
								mb={2}
								sx={{
									display: 'flex',
									flexDirection: 'row',
									flexWrap: 'wrap',
									width: '80%',
									[theme.breakpoints.down('sm')]: {
										width: '100%',
									},
								}}
							>
								{renderBlockSlotSquares()}
							</Box>
						) : null}
						{!isLite && (
							<Stack direction="row" alignItems="center" mb={1}>
								<Typography sx={{ fontWeight: 600 }}>
									{remainingBlocks === 0
										? t('account.addOns.modal.noBlockLeft', 'No blocks left')
										: t('account.addOns.modal.blocksLeft', {
												blocks:
													remainingBlocks !== unlimitedBlockSlots
														? remainingBlocks
														: allAddOnsAmount,
												defaultValue: '{{blocks}} blocks left',
										  })}
								</Typography>
								<CustomTooltip
									title={t(
										'account.addOns.modal.tooltip',
										'Customize your store with Blocks. With each paid plan you can select a certain amount of blocks for your account.',
									)}
								>
									<IconButton
										color="secondary"
										size="small"
										aria-label="info"
										sx={{ color: (theme) => theme.palette.text.primary }}
									>
										<RiInformationLine size={20} />
									</IconButton>
								</CustomTooltip>
							</Stack>
						)}
						<Typography sx={{ fontWeight: 400 }} variant="body2">
							{
								<Trans
									i18nKey="account.addOns.modal.currentPlan"
									defaults="You are currently on <shopPlan>{{shopPlan}}</shopPlan> ({{totalBlocks}} blocks)"
									values={{
										shopPlan: isLite ? 'Rentle Free' : shopPlanName,
										totalBlocks:
											totalBlocks !== unlimitedBlockSlots ? totalBlocks : allAddOnsAmount,
									}}
									components={{
										shopPlan: <span style={{ fontWeight: 500 }}></span>,
									}}
								/>
							}
						</Typography>
					</Box>
					{isInstalled ? (
						<Box>
							<Box
								sx={{
									color: theme.palette.primary.main,
									fontWeight: 500,
									textDecoration: 'underline',
								}}
								mb={1.5}
							>
								<RLink to={setupPage}>
									<Typography>
										{t('account.addOns.installedAddOns.goToSettings', 'Go to settings')}
									</Typography>
								</RLink>
							</Box>
							{renderHelpArticleLink()}
							<Box sx={{ mt: 8 }} onClick={() => setUninstallDialogOpen(true)}>
								<FeatureButton requiredFeature="ADD_ONS" variant="contained" color="error">
									{t('account.addOns.installedAddOns.uninstall', 'Uninstall')}
								</FeatureButton>
							</Box>
						</Box>
					) : (
						<>
							{!isLite && (
								<Box
									sx={{
										color: theme.palette.primary.main,
										fontWeight: 500,
										textDecoration: 'underline',
										width: '50%',
									}}
									mb={1.5}
								>
									<RLink to={Routes.BlockStoreTab('installed')}>
										<Typography>
											{t('account.addOns.modal.manageBlocks', 'Manage blocks')}
										</Typography>
									</RLink>
								</Box>
							)}
							{renderHelpArticleLink()}
							{!isIntegration && !isLite && (
								<Box sx={{ textDecoration: 'underline', width: '50%' }}>
									<Link
										rel="noopener noreferrer"
										target="_blank"
										href="https://www.rentle.io/demo"
										underline="none"
									>
										<Typography>{t('account.addOns.modal.bookADemoCTA', 'Book a demo')}</Typography>
									</Link>
								</Box>
							)}

							<Box
								sx={{
									mt: 4,
									[theme.breakpoints.down('sm')]: {
										position: 'fixed',
										borderTop: (theme) => `1px solid ${theme.palette.divider}`,
										padding: theme.spacing(4),
										bottom: 0,
										left: 0,
										right: 0,
									},
								}}
							>
								{isIntegration ? (
									<>
										<Link
											rel="noopener noreferrer"
											target="_blank"
											href="https://www.rentle.io/meetings/hi/integration-setup"
											underline="none"
										>
											<Button
												fullWidth={isMobile}
												color="primary"
												variant="contained"
												onClick={onClose}
											>
												{t('account.addOns.modal.bookADemoCTA', 'Book a demo')}
											</Button>
										</Link>
									</>
								) : (
									<FeatureButton
										color="primary"
										variant="contained"
										requiredFeature={
											remainingBlocks <= 0 ? ['PLAN_BILLING', 'ADD_ONS'] : ['ADD_ONS']
										}
										onClick={() => (remainingBlocks <= 0 ? push(Routes.PLANS) : handleInstall())}
										fullWidth={isMobile}
										disabled={loading}
									>
										{loading ? (
											<CircularProgress size={18} color="inherit" />
										) : remainingBlocks <= 0 ? (
											t('shop.billingView.upgradePlan')
										) : (
											t('account.addOns.modal.installCTA', 'Install now')
										)}
									</FeatureButton>
								)}
							</Box>
						</>
					)}
				</Grid>
			</Grid>
			<BackdropDialog
				isOpen={uninstallDialogOpen}
				onClose={() => setUninstallDialogOpen(false)}
				label={t('account.addOns.installedAddOns.dialogLabel', {
					addOn: addOnDetails.name,
					defaultValue: 'Uninstall {{addOn}}',
				})}
				body={
					<Box>
						<Typography variant="body2">{addOnDetails.uninstallNotice}</Typography>
						<Box mb={2} />
						<Typography variant="body2">
							{t(
								'account.addOns.installedAddOns.changesAppliedImmediately',
								'Changes are applied immediately. This cannot be undone.',
							)}
						</Typography>
					</Box>
				}
				variant="danger"
				width="xs"
				confirm={{
					label: t('account.addOns.installedAddOns.uninstall'),
					callback: handleUninstall,
					closeAfterDone: true,
				}}
				cancel={{
					label: t('common:actions.cancel'),
				}}
			/>
		</>
	);
};

const StyledImage = styled('img')(({ theme }) => ({
	height: '64px',
	width: '64px',
	margin: theme.spacing(5),
	alignSelf: 'flex-start',
	background: theme.palette.background.paper,
	[theme.breakpoints.down('sm')]: {
		height: '100px',
		width: '100px',
		border: `1px solid ${theme.palette.border.inputOutline}`,
		padding: theme.spacing(2),
		margin: 0,
	},
}));

export default AddOnModalContent;
